import { useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Severity counters — one per feature page.
// Each mirrors the counting logic inside that page's fetch function, so a past
// scan is scored exactly the way the page scores the current one.
// ---------------------------------------------------------------------------

const emptyCounts = () => ({ critical: 0, high: 0, medium: 0, low: 0, info: 0 });

// Default "Low"; substring match ("crit", "high", "med", "info")
function bump(counts, rawRisk) {
  const r = String(rawRisk || "Low").toLowerCase();
  if (r.includes("crit")) counts.critical++;
  else if (r.includes("high")) counts.high++;
  else if (r.includes("med")) counts.medium++;
  else if (r.includes("info")) counts.info++;
  else counts.low++;
}

const asList = (data) => (Array.isArray(data) ? data : []);

export const severityCounters = {
  // WHOIS, Reverse DNS, Credential Breach: one Risk per top-level item
  flat(data) {
    const counts = emptyCounts();
    asList(data).forEach((item) => bump(counts, item && item.Risk));
    return counts;
  },

  // DNS Lookup: records grouped by type inside Data[0]
  dns(data) {
    const counts = emptyCounts();
    const main = asList(data)[0];
    if (!main) return counts;
    ["A", "AAAA", "CNAME", "SOA", "MX", "NS", "TXT", "CAA"].forEach((typeKey) => {
      if (Array.isArray(main[typeKey])) {
        main[typeKey].forEach((rec) => bump(counts, rec && rec.Risk));
      }
    });
    return counts;
  },

  // Port Scan: Ports per IP; an IP with no ports counts as Informational
  ports(data) {
    const counts = emptyCounts();
    asList(data).forEach((item) => {
      const ports = (item && item.Ports) || [];
      if (ports.length === 0) counts.info++;
      else ports.forEach((p) => bump(counts, p && p.Risk));
    });
    return counts;
  },

  // Reverse IP: Domains per IP; an IP with no domains counts as Informational
  reverseIp(data) {
    const counts = emptyCounts();
    asList(data).forEach((item) => {
      const domains = (item && item.Domains) || [];
      if (domains.length === 0) counts.info++;
      else domains.forEach((d) => bump(counts, d && d.Risk));
    });
    return counts;
  },

  // Subdomain Finder: exact match, anything unknown is Informational
  subdomain(data) {
    const counts = emptyCounts();
    asList(data).forEach((item) => {
      if (!item) return;
      const r = String(item.Risk || "").toLowerCase();
      if (r === "critical") counts.critical++;
      else if (r === "high") counts.high++;
      else if (r === "medium" || r === "med") counts.medium++;
      else if (r === "low") counts.low++;
      else counts.info++;
    });
    return counts;
  },
};

// Same formula the feature pages use for their Score card
export function scoreFromCounts({ critical = 0, high = 0, medium = 0, low = 0, info = 0 }) {
  const total = critical + high + medium + low + info;
  if (total === 0) return 100;
  const rawScore = 100 - (critical * 12 + high * 6 + medium * 2 + low * 1);
  if (critical > 0) return Math.max(15, Math.min(39, rawScore));
  if (high > 0) return Math.max(40, Math.min(59, rawScore));
  if (medium > 0) return Math.max(60, Math.min(84, rawScore));
  return 98.42;
}

// ---------------------------------------------------------------------------
// useRiskTrendHistory
// Returns the score at the end of each of the last `pastDays` days (oldest
// first), plus the time of the latest scan for today. While loading: undefined.
//   [{ daysAgo, score | null, scannedAt | null, scannedThatDay }]
// score is null when no scan existed yet on that day.
// `refreshKey` re-fetches (pass the page's `loading` flag).
// ---------------------------------------------------------------------------
export function useRiskTrendHistory(featureID, counter, refreshKey, pastDays = 5) {
  const [history, setHistory] = useState(undefined);

  useEffect(() => {
    if (refreshKey === true) return undefined; // page still loading
    const orgID = localStorage.getItem("orgID");
    if (!orgID || !featureID || typeof counter !== "function") {
      setHistory([]);
      return undefined;
    }

    let cancelled = false;
    const now = new Date();

    // End of each past local day (23:59:59.999), oldest first, then "now" for today
    const days = [];
    for (let i = pastDays; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const end = i === 0 ? now : new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
      days.push({ daysAgo: i, start: start.getTime(), end: end.getTime() });
    }

    fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData/history`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        orgID,
        featureIDForServer: featureID,
        asOf: days.map((d) => d.end),
      }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const points = (res && Array.isArray(res.points) && res.points) || [];
        const scans = (res && res.scans) || {};
        const scoreCache = {};

        setHistory(
          days.map((d, idx) => {
            const pt = points[idx];
            if (!pt || !pt.scanId) {
              return { daysAgo: d.daysAgo, score: null, scannedAt: null, scannedThatDay: false };
            }
            if (scoreCache[pt.scanId] === undefined) {
              scoreCache[pt.scanId] = scoreFromCounts(counter(scans[pt.scanId]));
            }
            const scannedMs = pt.scannedAt ? new Date(pt.scannedAt).getTime() : null;
            return {
              daysAgo: d.daysAgo,
              score: scoreCache[pt.scanId],
              scannedAt: pt.scannedAt || null,
              scannedThatDay: scannedMs !== null && scannedMs >= d.start,
            };
          })
        );
      })
      .catch((err) => {
        console.warn("Risk trend history error:", err);
        if (!cancelled) setHistory([]);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureID, refreshKey, pastDays]);

  return history;
}
